import React, { useState, useRef } from 'react';
import { Upload, AlertCircle, CheckCircle2, UserPlus, Flag, Download, Trash2, Plus, X, RefreshCw, Swords, Users, Lock, Trophy, Heart, Edit2 } from 'lucide-react';
import { Player, GolfCourse } from '../types';
import { PlayerDeletion } from './players/PlayerDeletion';
import { PlayerAdd } from './players/PlayerAdd';
import { PlayerAddProvider } from '../contexts/PlayerAddContext';
import { usePlayerDeletion } from '../contexts/PlayerDeletionContext';
import { courses as defaultCourses } from '../data/courses';
import { RegistrationModule } from './registration';
import { useRegistration } from '../contexts/RegistrationContext';
import { SponsorManagement } from './sponsors/SponsorManagement';
import { resetAllData } from '../lib/localStorage';
import { CalendarRange, DollarSign } from 'lucide-react';

const initialPlayerForm: Player = {
  name: '',
  handicap: '',
  flight: '',
  team: '',
  matchNo: '',
  startingHole: '',
};

interface ImportDataProps {
  onPlayersImported: (players: Player[]) => void;
  onCoursesImported: (courses: GolfCourse[]) => void;
  initialPlayers: Player[];
  onCourseSelect: (courseId: string) => void;
  onTeeSelect: (teeName: string) => void;
  selectedCourseId: string;
  selectedTee: string;
  onFormatSelect: (format: 'stroke' | 'match') => void;
  selectedFormat: 'stroke' | 'match';
  onReset: () => void;
  isAdmin: boolean;
  onTournamentUpdate: (tournament: any) => void;
  selectedTournament: any;
  supabase: any;
  year?: string;
  isOfficialTournament?: boolean;
  onYearChange?: (year: string) => void;
  onOfficialTournamentChange?: (isOfficial: boolean) => void;
}

export function ImportData({ 
  onPlayersImported, 
  onCoursesImported, 
  initialPlayers,
  onCourseSelect,
  onTeeSelect,
  selectedCourseId,
  selectedTee,
  onFormatSelect,
  selectedFormat,
  onReset,
  isAdmin,
  onTournamentUpdate,
  selectedTournament,
  supabase,
  year = '2025',
  isOfficialTournament = true,
  onYearChange = () => {},
  onOfficialTournamentChange = () => {}
}: ImportDataProps) {
  const { isRegistering, startRegistration, completeRegistration, cancelRegistration, registeredMemberId, isNewMember } = useRegistration();
  const { error: deleteError } = usePlayerDeletion();
  const [showActivatePrompt, setShowActivatePrompt] = useState(false);
  const [registrationText, setRegistrationText] = useState(() => ({
    title: selectedTournament?.metadata?.registrationText?.title || 'Tournament Status',
    message: selectedTournament?.metadata?.registrationText?.message || 'This tournament is currently open for registration.',
  }));
  const [showCostConfig, setShowCostConfig] = useState(false);
  const [costConfig, setCostConfig] = useState(() => ({
    member: {
      amount: selectedTournament?.metadata?.cost?.member?.amount || 15000,
      description: selectedTournament?.metadata?.cost?.member?.description || 'Member Registration'
    },
    nonMember: {
      amount: selectedTournament?.metadata?.cost?.nonMember?.amount || 20000,
      description: selectedTournament?.metadata?.cost?.nonMember?.description || 'Non-Member Registration'
    },
    lateRegistration: {
      amount: selectedTournament?.metadata?.cost?.lateRegistration?.amount || 2000,
      description: selectedTournament?.metadata?.cost?.lateRegistration?.description || 'Late Registration Fee'
    }
  }));
  const [isEditingText, setIsEditingText] = useState(false);
  const [tempRegistrationText, setTempRegistrationText] = useState(registrationText);
  

  const [importType, setImportType] = useState<'players' | 'courses'>('players');
  const [importStatus, setImportStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter players for current tournament
  const tournamentPlayers = initialPlayers.filter(p => p.tournamentId === selectedTournament?.id);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTournament) return;

    setImportStatus('processing');
    setError(null);

    try {
      const text = await file.text();
      const rows = text.split('\n').filter(row => row.trim());

      const { data: members } = await supabase
        .from('members')
        .select('id, name')
        .order('name');

      if (!members) {
        throw new Error('Failed to fetch member profiles');
      }

      // Create map of member names to IDs
      const memberMap = new Map(members.map(m => [m.name.toLowerCase(), m.id]));
      
      const players = rows.slice(1).map(row => {
        const [name, team, handicap, matchNo, startingHole] = row.split(',').map(v => v.trim());
        const memberId = memberMap.get(name.toLowerCase());
        
        if (!memberId) {
          console.warn(`No matching member found for player: ${name}`);
          return null;
        }
        
        return {
          id: memberId,
          name,
          tournamentId: selectedTournament?.id,
          handicap: handicap ? parseFloat(handicap) : undefined,
          team,
          matchNo: matchNo ? parseInt(matchNo, 10) : undefined,
          startingHole: startingHole ? parseInt(startingHole, 10) : undefined
        };
      }).filter((p): p is Player => p !== null);
      
      onPlayersImported(players);
      setImportStatus('success');
    } catch (err) {
      console.error('Import error:', err);
      setError(err instanceof Error ? err.message : 'Failed to import file');
      setImportStatus('error');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle registration deadline change
  const handleRegistrationDeadlineChange = (date: string) => {
    if (!date || !selectedTournament) return;

    // Update local state first
    onTournamentUpdate({
      ...selectedTournament,
      registrationDeadline: date
    });

    // Sync with Supabase
    const syncToDatabase = async () => {
      try {
        const { error } = await supabase
        .from('tournaments')
        .update({ registration_deadline: date })
        .eq('id', selectedTournament.id);

        if (error) {
          console.error('Failed to update registration deadline:', error);
          throw error;
        }
      } catch (error) {
        console.error('Database sync error:', error);
        // Revert local state on error
        onTournamentUpdate({
          ...selectedTournament,
          registrationDeadline: selectedTournament.registrationDeadline
        });
      }
    };

    // Execute database sync
    syncToDatabase();
  };


  return (
    <div className="space-y-6">
      {/* Activation Confirmation Modal */}
      {showActivatePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-semibold">Activate Tournament</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to activate this tournament? This will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Close registration for new players</li>
                <li>Enable scoring for registered players</li>
                <li>Start the tournament</li>
              </ul>
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowActivatePrompt(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onTournamentUpdate({
                    ...selectedTournament,
                    status: 'active'
                  });
                  setShowActivatePrompt(false);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Activate Tournament
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration Modal */}
      {isRegistering && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <RegistrationModule
              selectedTournament={selectedTournament}
              onComplete={async (memberId, isNewMember) => {
                completeRegistration();
                // Add member to tournament if they're new or not already in it
                if (isNewMember || !initialPlayers.some(p => p.id === memberId)) {
                  const { data: member } = await supabase
                    .from('members')
                    .select('*')
                    .eq('id', memberId)
                    .single();

                  if (member) {
                    const newPlayer: Player = {
                      id: member.id,
                      name: member.name,
                      tournamentId: selectedTournament?.id,
                      handicap: member.season?.['2025']?.handicap || 0,
                      flight: member.season?.['2025']?.flight || 'A',
                      scores: [],
                      totalScore: 0
                    };
                    onPlayersImported([...initialPlayers, newPlayer]);
                  }
                }
              }}
              onCancel={cancelRegistration}
            />
          </div>
        </div>
      )}

      {/* Tournament Status Banner */}
      {selectedTournament && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-2 relative w-full"> 
              {isAdmin && (selectedTournament?.status === 'draft' || selectedTournament?.status === 'registration') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Deadline
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={(() => {
                        const deadline = selectedTournament.registrationDeadline || selectedTournament.startDate;
                        return deadline.split('T')[0];
                      })()}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = selectedTournament.registrationDeadline
                          ? selectedTournament.registrationDeadline.split('T')[1]
                          : '18:00:00.000Z';
                        handleRegistrationDeadlineChange(`${date}T${time}`);
                      }}
                      max={selectedTournament.startDate}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                    <input
                      type="time"
                      value={(() => {
                        const deadline = selectedTournament.registrationDeadline;
                        if (!deadline) return '18:00';
                        const time = deadline.split('T')[1].slice(0, 5);
                        return time;
                      })()}
                      onChange={(e) => {
                        const [hours, minutes] = e.target.value.split(':');
                        const date = (selectedTournament.registrationDeadline || selectedTournament.startDate).split('T')[0];
                        handleRegistrationDeadlineChange(`${date}T${hours}:${minutes}:00.000Z`);
                      }}
                      className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Set a deadline for tournament registration (UTC).
                  </p>
                </div>
              )}
              {isAdmin && (
                <button
                  onClick={() => setIsEditingText(true)}
                  className="absolute -right-2 -top-2 p-2 text-gray-400 hover:text-gray-600"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              <div className="text-sm font-medium text-gray-600">{registrationText.title}</div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                selectedTournament.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                selectedTournament.status === 'registration' ? 'bg-blue-100 text-blue-800' :
                selectedTournament.status === 'active' ? 'bg-green-100 text-green-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {selectedTournament.status === 'draft' ? 'Setup in Progress' :
                 selectedTournament.status === 'registration' ? 'Open for Registration' :
                 selectedTournament.status === 'active' ? 'Tournament in Progress' :
                 'Tournament Completed'}
              </span>
              {selectedTournament.status === 'registration' && (
                <p className="text-sm text-gray-500">
                  {registrationText.message}
                </p>
              )}
            </div>
            
            {/* Activation Button - Only show during registration phase for admins */}
            {selectedTournament.status === 'registration' && isAdmin && (
              <div className="mt-4">
                <button
                  onClick={() => setShowActivatePrompt(true)}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center justify-center gap-2"
                >
                  <Trophy className="w-4 h-4" />
                  Activate Tournament
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Start tournament scoring
                </p>
              </div>
            )}

            {/* Registration Button - Only show during registration phase */}
            {selectedTournament.status === 'registration' && !isAdmin && (
              <div className="mt-4">
                <button
                  onClick={startRegistration}
                  className={`w-full px-4 py-2 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center gap-2 ${
                    (() => {
                      const now = new Date();
                      const deadline = selectedTournament.registrationDeadline ? 
                        new Date(selectedTournament.registrationDeadline) :
                        new Date(selectedTournament.startDate);
                      
                      return now > deadline
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700';
                    })()
                  }`}
                  disabled={(() => {
                    const now = new Date();
                    const deadline = selectedTournament.registrationDeadline ? 
                      new Date(selectedTournament.registrationDeadline + 'Z') :
                      new Date(selectedTournament.startDate);
                    return now > deadline;
                  })()}
                >
                  <UserPlus className="w-4 h-4" />
                  {(() => {
                    const now = new Date();
                    const deadline = selectedTournament.registrationDeadline ? 
                      new Date(selectedTournament.registrationDeadline + 'Z') :
                      new Date(selectedTournament.startDate);
                    
                    return now > deadline
                      ? 'Registration Closed'
                      : 'Register for Tournament';
                  })()}
                </button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  {(() => {
                    const now = new Date();
                    const deadline = selectedTournament.registrationDeadline ? 
                      new Date(selectedTournament.registrationDeadline + 'Z') :
                      new Date(selectedTournament.startDate);
                    
                    if (now > deadline) {
                      return 'Registration period has ended';
                    }
                    
                    if (selectedTournament.registrationDeadline) {
                      const deadlineDate = new Date(selectedTournament.registrationDeadline + 'Z');
                      return `Registration closes on ${deadlineDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}`;
                    }
                    
                    return 'Registration closes when tournament starts';
                  })()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Text Editing Modal */}
      {isEditingText && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Edit Registration Text</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={tempRegistrationText.title}
                  onChange={(e) => setTempRegistrationText(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={tempRegistrationText.message}
                  onChange={(e) => setTempRegistrationText(prev => ({ ...prev, message: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setIsEditingText(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setRegistrationText(tempRegistrationText);
                    setIsEditingText(false);
                    // Update tournament metadata with new text
                    onTournamentUpdate({
                      ...selectedTournament,
                      metadata: {
                        ...selectedTournament.metadata,
                        registrationText: tempRegistrationText
                      }
                    });
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Year and Official Tournament Controls */}
      {isAdmin && selectedTournament?.status === 'draft' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-gray-500" />
              <select
                value={year}
                onChange={(e) => onYearChange(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                {[...Array(11)].map((_, i) => {
                  const yearNum = 2020 + i;
                  return (
                    <option key={yearNum} value={yearNum}>
                      {yearNum} - {yearNum === 2024 ? 'Feb-Jul' : 
                                 yearNum >= 2025 ? `Nov '${(yearNum-1).toString().slice(-2)} - May '${yearNum.toString().slice(-2)}` : 
                                 'Full Year'}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gray-500" />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={isOfficialTournament}
                  onChange={(e) => onOfficialTournamentChange(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Official HVGA Monthly Tournament
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Course Selection */}
      {isAdmin && selectedTournament?.status === 'draft' && selectedCourseId === '' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center py-8">
            <Flag className="w-12 h-12 text-primary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Select a Course
            </h3>
            <p className="text-gray-600 mb-6">
              Choose a golf course for this tournament to get started
            </p>
            <select
              value={selectedCourseId}
              onChange={(e) => onCourseSelect(e.target.value)}
              className="w-full max-w-md mx-auto rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">Select a course</option>
              {defaultCourses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.name} - {course.location}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      {isAdmin && selectedTournament?.status === 'draft' && selectedCourseId !== '' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Tournament Setup</h3>
              {selectedTournament?.metadata?.cost && (
              <button
                onClick={() => setShowCostConfig(true)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Configure Costs
              </button>
              )}
            </div>
            {selectedCourseId && selectedTee && !selectedTournament?.metadata?.cost && (
              <button
                onClick={() => setShowCostConfig(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Configure Tournament Costs
              </button>
            )}
            {selectedCourseId && selectedTee && selectedTournament?.metadata?.cost && (
              <button
                onClick={() => {
                  onTournamentUpdate({
                    ...selectedTournament,
                    status: 'registration'
                  });
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
              >
                <Trophy className="w-4 h-4" />
                Open Registration
              </button>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Course
              </label>
              <select
                value={selectedCourseId}
                onChange={(e) => onCourseSelect(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Select a course</option>
                {defaultCourses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.name} - {course.location}
                  </option>
                ))}
              </select>
            </div>

            {selectedCourseId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Tees
                </label>
                <select
                  value={selectedTee}
                  onChange={(e) => onTeeSelect(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  {defaultCourses
                    .find(c => c.id === selectedCourseId)
                    ?.tees.map(tee => (
                      <option key={tee.name} value={tee.name}>
                        {tee.name} ({tee.rating} / {tee.slope})
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cost Configuration Modal */}
      {showCostConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-6">Tournament Cost Configuration</h3>
            
            <div className="space-y-6">
              {/* Member Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Member Registration Cost
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.member.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      member: {
                        ...prev.member,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Non-Member Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Non-Member Registration Cost
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.nonMember.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      nonMember: {
                        ...prev.nonMember,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Late Registration Fee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Late Registration Fee
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.lateRegistration.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      lateRegistration: {
                        ...prev.lateRegistration,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Applied after registration deadline
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCostConfig(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onTournamentUpdate({
                      ...selectedTournament,
                      metadata: {
                        ...selectedTournament.metadata,
                        cost: costConfig
                      }
                    });
                    setShowCostConfig(false);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Save Costs
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Configuration Modal */}
      {showCostConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-6">Tournament Cost Configuration</h3>
            
            <div className="space-y-6">
              {/* Member Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Member Registration Cost
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.member.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      member: {
                        ...prev.member,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Non-Member Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Non-Member Registration Cost
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.nonMember.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      nonMember: {
                        ...prev.nonMember,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Late Registration Fee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Late Registration Fee
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.lateRegistration.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      lateRegistration: {
                        ...prev.lateRegistration,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Applied after registration deadline
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCostConfig(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onTournamentUpdate({
                      ...selectedTournament,
                      metadata: {
                        ...selectedTournament.metadata,
                        cost: costConfig
                      }
                    });
                    setShowCostConfig(false);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Save Costs
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cost Configuration Modal */}
      {showCostConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-6">Tournament Cost Configuration</h3>
            
            <div className="space-y-6">
              {/* Member Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Member Registration Cost
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.member.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      member: {
                        ...prev.member,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Non-Member Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Non-Member Registration Cost
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.nonMember.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      nonMember: {
                        ...prev.nonMember,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Late Registration Fee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Late Registration Fee
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={(costConfig.lateRegistration.amount / 100).toFixed(2)}
                    onChange={(e) => setCostConfig(prev => ({
                      ...prev,
                      lateRegistration: {
                        ...prev.lateRegistration,
                        amount: Math.round(parseFloat(e.target.value) * 100)
                      }
                    }))}
                    className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Applied after registration deadline
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowCostConfig(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onTournamentUpdate({
                      ...selectedTournament,
                      metadata: {
                        ...selectedTournament.metadata,
                        cost: costConfig
                      }
                    });
                    setShowCostConfig(false);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                >
                  Save Costs
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sponsor Management */}
      {isAdmin && selectedTournament?.status === 'draft' && selectedCourseId !== '' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary-500" />
            Tournament Sponsor
          </h3>
          <SponsorManagement 
            tournamentId={selectedTournament.id}
            isAdmin={isAdmin}
            selectedTournament={selectedTournament}
            onTournamentUpdate={onTournamentUpdate}
          />
        </div>
      )}

      {/* Format Selection */}
      {isAdmin && <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Tournament Format</h3>
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => onFormatSelect('stroke')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              selectedFormat === 'stroke'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-primary-500'
            }`}
          >
            <Flag className="w-6 h-6 mb-2" />
            <div className="font-medium">Stroke Play</div>
            <div className="text-sm text-gray-600">
              Traditional scoring format
            </div>
          </button>
          <button
            onClick={() => onFormatSelect('match')}
            className={`p-4 rounded-lg border-2 transition-colors ${
              selectedFormat === 'match'
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-primary-500'
            }`}
          >
            <Swords className="w-6 h-6 mb-2" />
            <div className="font-medium">Match Play</div>
            <div className="text-sm text-gray-600">
              Head-to-head competition
            </div>
          </button>
        </div>
      </div>}

      {/* Player Import */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Player Management</h3>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <>
                <PlayerDeletion 
                  tournamentId={selectedTournament?.id} 
                  isAdmin={isAdmin} 
                />
              </>
            )}
            {!isAdmin && (
              <div className="text-gray-400 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Admin Only
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              ref={fileInputRef}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-medium
                file:bg-primary-50 file:text-primary-700
                hover:file:bg-primary-100
              "
            />
            <a
              href="/course-template.csv"
              download
              className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
            >
              <Download className="w-4 h-4" />
              Template
            </a>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error || deleteError}
            </div>
          )}

          {importStatus === 'success' && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Players imported successfully
            </div>
          )}

          {/* Manual Player Entry Form */}
          <div className="mt-4 p-4 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-4">Add Player Manually</h4>
            <PlayerAddProvider onPlayersUpdate={onPlayersImported} initialPlayers={initialPlayers}>
              <PlayerAdd tournamentId={selectedTournament?.id || ''} />
            </PlayerAddProvider>
          </div>

          {initialPlayers.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Tournament Players ({tournamentPlayers.length})
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {tournamentPlayers.map(player => (
                  <div
                    key={player.id}
                    className="p-3 rounded-lg border border-gray-200 flex items-center justify-between"
                  >
                    <div>
                      <div className="font-medium">{player.name}</div>
                      <div className="text-sm text-gray-600">
                        {player.handicap ? `HC: ${player.handicap}` : 'SCR'}
                        {player.flight && ` • ${player.flight} Flight`}
                        {player.team && ` • Team ${player.team}`}
                        {player.matchNo && ` • Match ${player.matchNo}`}
                        {player.division && ` • ${player.division === 'senior' ? 'Senior' : 'Regular'}`}
                      </div>
                    </div>
                    <PlayerDeletion
                      player={player}
                      tournamentId={selectedTournament?.id}
                      isAdmin={isAdmin}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
